import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import SaveAlt from "@mui/icons-material/SaveAlt";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";

import "./AdminComponents.scss";
import { getBedTypes, updateBedType, deleteBedType } from "../api/bedMasterApi";
import { dataGridSx, modalBoxSx, confirmButtonSx, cancelButtonSx } from "./bedMasterStyles";

/**
 * Ward List — the grid half of the Ward Management screen. Same shape as
 * UserDetails / DoctorList: a DataGrid with per-row Edit and Delete, both
 * driven by the `code`.
 *
 * The UI calls these wards; the backend entity is still a bed type
 * (ipd_bedtype/), so the API and prop names keep that wording.
 */
function BedTypeList({ refreshList }) {
  const [bedTypes, setBedTypes] = useState([]);

  const [codeToDelete, setCodeToDelete] = useState(null);
  const [editingBedType, setEditingBedType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchBedTypes = () => {
    getBedTypes()
      .then((list) =>
        setBedTypes(
          list.map((item) => ({
            code: item.code ?? "-",
            // name / description go back out in the update payload, so they
            // hold the raw value — a "-" placeholder here would be saved as a
            // literal "-". The columns render the dash instead.
            name: item.name ?? "",
            description: item.description ?? "",
            daily_charge: item.daily_charge ?? "-",
            is_active: item.is_active !== undefined ? item.is_active : true,
          })),
        ),
      )
      .catch((err) => {
        console.error("getBedTypes:", err);
        toast.error("Could not load wards");
      });
  };

  useEffect(() => {
    fetchBedTypes();
  }, [refreshList]);

  const handleConfirmDelete = () => {
    const code = codeToDelete;
    deleteBedType(code)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Ward Deleted Successfully");
          // Drop the row locally instead of re-listing: the delete is a soft
          // delete and GET only returns is_active=True rows, so a refetch would
          // return exactly this list minus this row.
          setBedTypes((rows) => rows.filter((row) => row.code !== code));
        } else {
          toast.error(res?.message || "Could not delete ward");
        }
      })
      .catch((err) => {
        console.error("deleteBedType:", err);
        toast.error(err?.response?.data?.message || "Could not delete ward");
      })
      .finally(() => setCodeToDelete(null));
  };

  const handleEditSubmit = () => {
    setIsSaving(true);
    updateBedType({
      code: editingBedType.code,
      name: editingBedType.name,
      daily_charge: Number(editingBedType.daily_charge).toFixed(2),
      description: editingBedType.description,
      is_active: editingBedType.is_active,
    })
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Ward Updated Successfully");
          setEditingBedType(null);
          fetchBedTypes();
        } else {
          toast.error(res?.message || "Could not update ward");
        }
      })
      .catch((err) => {
        console.error("updateBedType:", err);
        toast.error(err?.response?.data?.message || "Could not update ward");
      })
      .finally(() => setIsSaving(false));
  };

  const columns = [
    {
      field: "name",
      headerName: "Ward Name",
      width: 200,
      valueFormatter: (params) => params?.value || "-",
    },
    { field: "code", headerName: "Code", width: 120 },
    { field: "daily_charge", headerName: "Daily Charge", width: 130 },
    {
      field: "description",
      headerName: "Description",
      width: 250,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <div
          style={{ color: params.value ? "green" : "red", fontWeight: "bold" }}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <button
            onClick={() => setEditingBedType({ ...params.row })}
            style={{ color: "blue", fontWeight: "bold", marginRight: "8px" }}
          >
            Edit
          </button>
          <button
            onClick={() => setCodeToDelete(params.row.code)}
            style={{ color: "red", fontWeight: "bold" }}
          >
            Delete
          </button>
        </>
      ),
    },
  ];

  const generatePdf = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [["Ward Name", "Code", "Daily Charge", "Description", "Status"]],
      body: bedTypes.map((bt) => [
        bt.name || "-",
        bt.code,
        bt.daily_charge,
        bt.description || "-",
        bt.is_active ? "Active" : "Inactive",
      ]),
    });
    doc.save("Ward_List.pdf");
  };

  const generateCsvData = () =>
    bedTypes.map((bt) => ({
      "Ward Name": bt.name || "-",
      Code: bt.code,
      "Daily Charge": bt.daily_charge,
      Description: bt.description || "-",
      Status: bt.is_active ? "Active" : "Inactive",
    }));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "auto",
        width: "100%",
        maxWidth: "1200px",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%" }}>
        <h4 style={{ textAlign: "center" }}>Ward List</h4>

        <div className="display-flex">
          <DataGrid
            rows={bedTypes}
            columns={columns}
            getRowId={(row) => row.code}
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{ display: "flex", gap: 1 }}
                  >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "center", sm: "flex-end" },
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={generatePdf}
                      startIcon={<SaveAlt />}
                    >
                      Download PDF
                    </Button>
                    <CSVLink
                      data={generateCsvData()}
                      filename={"ward_list.csv"}
                      target="_blank"
                    >
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<SaveAlt />}
                      >
                        Download CSV
                      </Button>
                    </CSVLink>
                  </Grid>
                </Grid>
              ),
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10]}
            getRowClassName={() => "custom-row"}
            sx={dataGridSx}
          />
        </div>
      </div>

      <Modal
        open={!!codeToDelete}
        onClose={() => setCodeToDelete(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="confirm-delete-bedtype-modal"
      >
        <Box sx={modalBoxSx}>
          <h3 id="confirm-delete-bedtype-modal" style={{ marginTop: 0, fontSize: "32px" }}>
            Confirm Deletion
          </h3>
          <p style={{ fontSize: "20px" }}>
            Are you sure you want to delete ward <b>{codeToDelete}</b>?
          </p>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
              mt: "10%",
            }}
          >
            <Button onClick={() => setCodeToDelete(null)} sx={cancelButtonSx}>
              CANCEL
            </Button>
            <Button onClick={handleConfirmDelete} sx={confirmButtonSx}>
              CONFIRM DELETION
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={!!editingBedType}
        onClose={() => setEditingBedType(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="edit-bedtype-modal"
      >
        <Box sx={{ ...modalBoxSx, maxWidth: "500px" }}>
          <h3 id="edit-bedtype-modal" style={{ marginTop: 0 }}>
            Edit Ward
          </h3>
          {editingBedType && (
            <form>
              {/* Code identifies the ward on update — it is shown for
                  context but never edited. */}
              <TextField
                label="Ward Code"
                value={editingBedType.code}
                disabled
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              <TextField
                label="Ward Name"
                value={editingBedType.name}
                onChange={(e) =>
                  setEditingBedType({ ...editingBedType, name: e.target.value })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              <TextField
                label="Daily Charge"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={editingBedType.daily_charge}
                onChange={(e) =>
                  setEditingBedType({
                    ...editingBedType,
                    daily_charge: e.target.value,
                  })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              <TextField
                label="Description"
                value={editingBedType.description}
                onChange={(e) =>
                  setEditingBedType({
                    ...editingBedType,
                    description: e.target.value,
                  })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              <TextField
                select
                label="Status"
                value={editingBedType.is_active ? "true" : "false"}
                onChange={(e) =>
                  setEditingBedType({
                    ...editingBedType,
                    is_active: e.target.value === "true",
                  })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
              <Button
                type="submit"
                disabled={isSaving}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditSubmit();
                }}
                variant="contained"
                style={{
                  backgroundColor: "#213360",
                  color: "#fff",
                  border: "2px solid #213360",
                  borderRadius: "20px",
                  marginTop: "10px",
                }}
              >
                Save Changes
              </Button>
            </form>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default BedTypeList;
